// Tiny itchy/scratchy SFX synthesized via WebAudio — no dependencies, no
// assets. Designed to be played on user gestures only so autoplay policies
// stay happy.

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  try {
    if (!ctx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    }
    if (ctx.state === "suspended") {
      void ctx.resume();
    }
    return ctx;
  } catch {
    return null;
  }
}

// Build a short buffer of noise shaped into two tiny scratchy "hits"
// with a bandpass filter — under 140 ms total. Same recipe for both
// theme directions so the app has one consistent little signature.
export function playScratchy() {
  const audio = getCtx();
  if (!audio) return;

  const duration = 0.14;
  const sampleRate = audio.sampleRate;
  const frameCount = Math.floor(sampleRate * duration);
  const buffer = audio.createBuffer(1, frameCount, sampleRate);
  const data = buffer.getChannelData(0);

  for (let i = 0; i < frameCount; i++) {
    const t = i / frameCount;
    // Two micro bursts: one at the start, one at ~55% through.
    const burst1 = Math.max(0, 1 - t * 8);
    const burst2 = Math.max(0, 1 - Math.abs(t - 0.55) * 14);
    const env = Math.max(burst1, burst2);
    // Pink-ish noise approximation with a gentle bias — keeps it itchy,
    // not harsh.
    const noise = (Math.random() * 2 - 1) * 0.6;
    data[i] = noise * env;
  }

  const src = audio.createBufferSource();
  src.buffer = buffer;

  const bandpass = audio.createBiquadFilter();
  bandpass.type = "bandpass";
  bandpass.frequency.value = 2600;
  bandpass.Q.value = 0.9;

  const gain = audio.createGain();
  gain.gain.value = 0.22;

  src.connect(bandpass);
  bandpass.connect(gain);
  gain.connect(audio.destination);

  src.start();
  src.stop(audio.currentTime + duration + 0.02);
}
